import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Grid } from '@material-ui/core';

import { useGlobal } from '../../../../contexts/Global/context';

import Accordion from '../../../../components/Accordion';
import Alert from '../../../../components/Alert';
import Button from '../../../../components/Button';
import Divider from '../../../../components/Divider';
import Input from '../../../../components/Inputs/Input';
import Select from '../../../../components/Inputs/Select';

import validators from '../../../../utils/defaultValidators';
import validate from '../../../../utils/validateForm';

import schema from './schema';
import translateToSchema from './translate';

import { checkIsSupport, clearInputMask, cloneObject } from '../../../../utils';

import { getEnderecoByCep, getEstados, getMunicipios, postLotacoes, putLotacoes } from '../../../../services';

import { PageContainer } from '../../../../styles/styles';

import * as S from './styles';

const addressSchema = {
    estado: {
        sigla: '',
        municipios: [
            {
                nome: '',
                bairros: [
                    {
                        nome: '',
                    },
                ],
            },
        ],
    },
    logradouro: '',
    tipoLogradouro: '',
};

const EDIT_MESSAGE = 'Você alterou dados de uma lotação!';
const ADD_MESSAGE = 'Você adicionou uma lotação!';

function FormLotacao({ location: { dataLotacao } }) {
    const { dataActiveContract, setShowSupportModal } = useGlobal();

    const history = useHistory();

    const translatedData = dataLotacao && translateToSchema(dataLotacao);

    const [dataForm, setDataForm] = useState(translatedData ? cloneObject(translatedData) : cloneObject(schema));
    const [cepLoading, setCepLoading] = useState(false);
    const [alreadySearchedCep, setAlreadySearchedCep] = useState(false);
    const [alerts, setAlerts] = useState([]);

    function handleInputBlur(event) {
        const { id, name, value } = event.target;
        const key = id || name;

        const form = cloneObject(dataForm);

        if (['cellphone', 'comercialPhone'].includes(key) && form[key].value?.length <= 1) {
            form[key].value = '';
            form[key].unmaskedValue = '';
        }

        // Valida as regras de negocio do campo e, caso haja erro, altera o indice error
        if (form[key].required || (!form[key].required && !!form[key].value)) {
            if (typeof validators[key] === 'function') validators[key](form);
        }

        // Valida os campos obrigatorios sem valores
        if (form[key].required && !value) {
            form[key].error = form[key].requiredMessage || 'Preencha com um valor válido.';
        }

        setDataForm({ ...form });
    }

    async function handleChangeState(state) {
        const municipios = (await getMunicipios(state)) || [];

        return municipios;
    }

    async function handleChangeCep(event) {
        const { value, unmaskedValue } = event;

        setCepLoading(true);
        let foundCep = true;
        const response = await getEnderecoByCep(unmaskedValue) || [];
        const zips = []
        // Para zerar o endereço caso seja digitado um cep inválido
        if (response.estado.municipios[0].bairros[0].nome === null) {
            zips.push({ ...addressSchema });
            foundCep = false;
        }

        const dataAddress = {
            address: {
                ...dataForm.address,
                value: response.tipoLogradouro ? `${response.tipoLogradouro}. ${response.logradouro}` : '',
            },
            hood: { ...dataForm.hood, value: response.estado.municipios[0].bairros[0].nome },
            uf: { ...dataForm.uf, value: response.estado.sigla, lookup: response.estado.nome, sigla: response.estado.sigla },
            city: {
                ...dataForm.city,
                value: response.estado.municipios[0].codigo,
                lookup: response.estado.municipios[0].nome,
            },
            cep: {
                ...dataForm.cep,
                unmaskedValue,
                value,
                error: !foundCep && 'Os dados de endereço não foram localizados a partir do CEP informado',
            },
        };

        if (foundCep) {
            dataAddress.address.error = '';
            dataAddress.hood.error = '';
            dataAddress.uf.error = '';
            dataAddress.city.error = '';
        }

        setCepLoading(false);
        setAlreadySearchedCep(true);

        return dataAddress;
    }

    async function handleChangeInput(event) {
        const { id, value, unmaskedValue, lookup, ...rest } = event;

        let field = { ...cloneObject(dataForm[id]), value, lookup, unmaskedValue, error: '', ...rest };

        const city = cloneObject(dataForm.city);

        if (id === 'uf') {
            const municipios = await handleChangeState(value);

            city.error = '';
            city.value = '';
            city.options = municipios;
        }

        let dataAddress = {};

        if (id === 'cep' && unmaskedValue.length === 8 && unmaskedValue !== dataForm.cep.value) {
            dataAddress = (await handleChangeCep(event)) || {};

            if (dataAddress.uf.value) document.getElementById('addressNumber').focus();

            const municipios = await handleChangeState(dataAddress.uf.value);

            dataAddress.city.options = municipios;

            field = dataAddress.cep;
        }

        const newDataForm = {
            ...dataForm,
            city,
            ...dataAddress,
            [id]: field,
        };

        setDataForm(newDataForm);
    }

    async function handleFinish() {
        const { validatedForm, isValid } = validate(dataForm);

        setDataForm({
            ...validatedForm,
        });

        if (!isValid) {
            setTimeout(() => {
                const firstElementWithError = document.querySelector('.error');

                if (firstElementWithError) {
                    firstElementWithError.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }, 300);

            return;
        }

        if (checkIsSupport()) {
            setShowSupportModal(true);
            return;
        }

        const [dddCellphone, cellphone] = dataForm.cellphone.value
            ? dataForm.cellphone.value.replace(/\(|\)/g, '').split(' ')
            : ['', ''];
        const [dddComercialPhone, comercialPhone] = dataForm.comercialPhone.value
            ? dataForm.comercialPhone.value.replace(/\(|\)/g, '').split(' ')
            : ['', ''];

        const payload = {
            CodigoLotacao: dataForm.code.value,
            NomeLotacao: dataForm.name.value,
            SiglaLotacao: dataForm.initials.value,
            NumeroCNPJ: clearInputMask(dataForm.cnpj.value),
            NomeRazaoSocial: dataForm.socialReason.value,
            NumeroInscricaoEstadual: dataForm.register.value,
            NumeroCEP: dataForm.cep.unmaskedValue,
            NomeLogradouro: dataForm.address.value,
            NumeroEndereco: dataForm.addressNumber.value,
            Complemento: dataForm.complement.value,
            NomeBairro: dataForm.hood.value,
            CodigoMunicipio: dataForm.city.value,
            NomeMunicipio: dataForm.city.lookup,
            SiglaUF: dataForm.uf.value,
            NumeroDDDTelefone1: dddComercialPhone,
            NumeroTelefone1: comercialPhone,
            NumeroRamalTelefone1: dataForm.ramal.value,
            NumeroDDDCelular: dddCellphone,
            NumeroCelular: cellphone,
            EnderecoEmail: dataForm.email.value,
            ContatoLotacao: dataForm.responsible.value,
        };

        let response = false;

        function handleError(error = {}) {
            setAlerts([
                {
                    type: 1,
                    title: `Lotação não pôde ser ${dataLotacao ? 'atualizada' : 'criada'}.${
                        error.errorDescription ? ` Motivo: ${error.errorDescription}` : ''
                    }`,
                },
            ]);
            setTimeout(() => {
                window.document.getElementById('page-content').scrollTo(0, 0);
            }, 100);
        }

        try {
            if (dataLotacao) {
                payload.CodigoTSContrato = dataLotacao.codigoTSContrato;

                response = await putLotacoes(dataLotacao.codigoLotacaoTS, dataActiveContract.numero, payload);
            } else {
                response = await postLotacoes(dataActiveContract.numero, payload);
            }

            if (!response.errorDescription) {
                history.push({
                    pathname: './selecionar_lotacao',
                    cameFromForm: true,
                    message: { type: 3, title: dataLotacao ? EDIT_MESSAGE : ADD_MESSAGE },
                });
            } else {
                handleError(response);
            }
        } catch (error) {
            handleError();
        }
    }

    useEffect(() => {
        async function loadStatesAndCities() {
            const estados = (await getEstados()) || [];
            const municipios = dataLotacao ? await handleChangeState(dataLotacao.siglaUF) : [];

            const initialValues = {
                uf: { ...dataForm.uf, options: estados },
                city: { ...dataForm.city, options: municipios },
            };

            setDataForm({
                ...dataForm,
                ...initialValues,
            });
        }

        loadStatesAndCities();
    }, []);

    return (
        <PageContainer>
            {alerts.map((alert, index) => (
                <Alert key={() => index} type={alert.type} title={alert.title} />
            ))}

            <Accordion title="Dados da lotação" hasCollapse={false}>
                <Grid container spacing={3}>
                    {!!dataLotacao && (
                        <>
                            <Grid item xs={12} md={6}>
                                <S.CustomInput
                                    field={dataForm.code}
                                    onChange={handleChangeInput}
                                    onBlur={handleInputBlur}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} />
                        </>
                    )}
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.name} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.initials} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.cnpj} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.socialReason} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.register} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.responsible} onChange={handleChangeInput} onBlur={handleInputBlur} />
                    </Grid>

                    <Divider />

                    <Grid item xs={12} md={6}>
                        <Input
                            field={{
                                ...dataForm.cep,
                                disabled: cepLoading,
                                loading: cepLoading,
                                icon: { name: 'search', type: 'round' },
                            }}
                            onBlur={handleInputBlur}
                            onChange={handleChangeInput}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} />

                    <Grid item xs={12} md={6}>
                        <S.CustomInput
                            field={{
                                ...dataForm.address,
                                disabled: !alreadySearchedCep,
                            }}
                            onBlur={handleInputBlur}
                            onChange={handleChangeInput}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.addressNumber} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.complement} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <S.CustomInput
                            field={{
                                ...dataForm.hood,
                                disabled: !alreadySearchedCep,
                            }}
                            onBlur={handleInputBlur}
                            onChange={handleChangeInput}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            field={{
                                ...dataForm.uf,
                                disabled: !alreadySearchedCep,
                            }}
                            onBlur={handleInputBlur}
                            onChange={handleChangeInput}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            field={{
                                ...dataForm.city,
                                disabled: !alreadySearchedCep,
                            }}
                            onBlur={handleInputBlur}
                            onChange={handleChangeInput}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.email} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.cellphone} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.comercialPhone} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Input field={dataForm.ramal} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    </Grid>
                </Grid>
            </Accordion>

            <Grid container direction="row" justifyContent="flex-end" alignItems="center">
                <Button id="backStep" type="secondary" style={{ marginRight: '24px' }} onClick={() => history.goBack()}>
                    Voltar
                </Button>

                <Button id="continueStep" type="primary" style={{ marginRight: '12px' }} onClick={handleFinish}>
                    Finalizar
                </Button>
            </Grid>
        </PageContainer>
    );
}

export default FormLotacao;
